import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock3, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get("/services/catalog").then((response) => {
      if (!active) return;
      const found = (response.data || []).find((item) => item.slug === slug && item.active !== false);
      setService(found || null);
    }).catch(() => setService(null)).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug]);

  const packages = useMemo(() => Array.isArray(service?.packages) ? service.packages : [], [service]);

  if (loading) return <div className="container-app py-12"><div className="h-40 animate-pulse rounded-3xl bg-slate-100" /></div>;
  if (!service) return <Navigate to="/univers/services" replace />;

  const description = (service.description || `Service ${service.name} proposé via Team Work Africa.`).slice(0, 180);
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description,
    url: `https://teamwork-africa.com/services/${service.slug}`,
    provider: {
      "@type": "Organization",
      name: "Team Work Africa",
      url: "https://teamwork-africa.com/",
    },
    offers: packages.length ? packages.map((pkg) => ({
      "@type": "Offer",
      name: pkg.name,
      price: Number(pkg.price || 0),
      priceCurrency: "USD",
      description: pkg.duration || undefined,
      url: `https://teamwork-africa.com/services/${service.slug}`,
    })) : undefined,
  };

  return (
    <>
      <Seo
        title={`${service.name} | Services Team Work Africa`}
        description={description}
        path={`/services/${service.slug}`}
        schema={schema}
      />
    <div className="min-h-screen bg-[#f7f8fa] pb-20">
      <div className="container-app max-w-6xl py-5 sm:py-9">
        <Link to="/univers/services" className="inline-flex items-center gap-2 text-sm font-bold text-brand-navy"><ArrowLeft className="h-4 w-4" /> Services</Link>

        <section className="mt-4 overflow-hidden rounded-[2rem] bg-brand-navy p-6 text-white shadow-xl sm:p-9">
          <p className="text-[11px] font-black uppercase tracking-[.2em] text-brand-orange">Service TeamWork</p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-extrabold leading-tight text-white sm:text-5xl">{service.name}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 sm:text-base">{service.description}</p>
          <div className="mt-5 flex flex-wrap gap-2 text-xs font-bold text-white/85">
            <span className="rounded-full bg-white/10 px-3 py-2">Prix visibles</span>
            <span className="rounded-full bg-white/10 px-3 py-2">Délai annoncé</span>
            <span className="rounded-full bg-white/10 px-3 py-2">Paiement après validation</span>
          </div>
        </section>

        {packages.length ? <section className="mt-7">
          <div className="mb-4">
            <p className="text-[11px] font-black uppercase tracking-[.18em] text-brand-orange">Choisissez votre formule</p>
            <h2 className="mt-1 font-display text-2xl font-extrabold text-brand-navy">Prix et délais annoncés avant l’appel</h2>
            <p className="mt-1 text-sm text-slate-500">Choisissez une base tarifaire, envoyez vos coordonnées, puis TeamWork confirme le périmètre avant paiement.</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            {packages.map((pkg, index) => {
              const query = new URLSearchParams({
                service: service.slug,
                service_name: service.name,
                package: pkg.name,
                package_price: String(pkg.price),
                package_duration: pkg.duration || "",
              }).toString();
              return <article key={pkg.name} className={`relative rounded-3xl border bg-white p-5 shadow-sm ${index === 1 ? "border-brand-orange ring-2 ring-orange-100" : "border-slate-200"}`}>
                {index === 1 && <span className="absolute right-4 top-4 rounded-full bg-brand-orange px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-white">Recommandé</span>}
                <h3 className="pr-20 font-display text-xl font-extrabold text-brand-navy">{pkg.name}</h3>
                <div className="mt-4 flex items-end gap-1"><span className="font-display text-4xl font-extrabold text-brand-navy">{pkg.price}</span><span className="pb-1 text-sm font-bold text-slate-500">USD</span></div>
                <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-500"><Clock3 className="h-4 w-4" /> {pkg.duration}</div>
                <div className="mt-5 space-y-2.5">{(pkg.features || []).map((feature) => <div key={feature} className="flex gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /><span>{feature}</span></div>)}</div>
                <Link to={`/services/commander?${query}`} className={`mt-6 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-extrabold ${index === 1 ? "bg-brand-orange text-white" : "bg-brand-navy text-white"}`}>Choisir cette formule <ArrowRight className="h-4 w-4" /></Link>
              </article>;
            })}
          </div>
        </section> : <section className="mt-7 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-2xl font-extrabold text-brand-navy">Parlez-nous de votre besoin</h2>
          <p className="mt-2 text-sm text-slate-500">Nous préparons un devis adapté à votre projet.</p>
          <Link to={`/demandes/nouvelle?sector=services&service=${encodeURIComponent(service.slug)}&context=1`} className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-brand-orange px-5 text-sm font-extrabold text-white">Demander un devis <ArrowRight className="h-4 w-4" /></Link>
        </section>}

        <section className="mt-7 grid gap-3 sm:grid-cols-3">
          {["Vous choisissez la formule et laissez vos coordonnées", "Nous validons le besoin et envoyons le lien de paiement", "La production démarre après validation"].map((text) => <div key={text} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4"><ShieldCheck className="h-5 w-5 shrink-0 text-brand-orange" /><p className="text-sm font-semibold text-brand-navy">{text}</p></div>)}
        </section>
      </div>
    </div>
    </>
  );
}
